import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { loadGooglePayApi, loadPayPal } from '../../lib/paypal'
import type { Locale, PayPalConfig } from '../../lib/api/types'

interface WalletProps {
  config: PayPalConfig
  locale: Locale
  amountCents: number
  createOrder: () => Promise<string>
  onApprove: (orderID: string) => Promise<void>
  onError?: (e: unknown) => void
  onEligible?: () => void
}

const MERCHANT_NAME = 'Garage San Marco'

/** Express wallet buttons (Apple Pay + Google Pay), shown above the method list when supported. */
export function WalletExpress(props: Omit<WalletProps, 'onEligible'> & { preview?: boolean }) {
  const { t } = useTranslation()
  const [eligible, setEligible] = useState(0)
  if (props.preview)
    // test/demo mode: PayPal has no real SDK here, so show a static Apple Pay mock (no charge)
    return (
      <div className="space-y-2.5">
        <div className="flex h-11 items-center justify-center gap-1.5 rounded-md bg-black text-base font-semibold text-white">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
            <path d="M17.05 12.04c-.03-2.6 2.13-3.85 2.22-3.91-1.21-1.77-3.1-2.01-3.77-2.04-1.6-.16-3.13.94-3.94.94-.81 0-2.07-.92-3.41-.9-1.75.03-3.37 1.02-4.27 2.59-1.82 3.16-.47 7.84 1.31 10.41.87 1.26 1.91 2.67 3.27 2.62 1.31-.05 1.81-.85 3.4-.85 1.58 0 2.03.85 3.42.82 1.41-.02 2.31-1.28 3.17-2.55 1-1.46 1.41-2.88 1.43-2.95-.03-.01-2.74-1.05-2.77-4.17M14.5 4.5c.72-.87 1.2-2.08 1.07-3.29-1.03.04-2.28.69-3.02 1.56-.66.77-1.24 2-1.09 3.18 1.15.09 2.32-.58 3.04-1.45" />
          </svg>
          Pay
        </div>
        <div className="relative flex items-center gap-3 pt-1">
          <span className="h-px flex-1 bg-ink-100" />
          <span className="text-xs font-medium uppercase tracking-wide text-ink-400">{t('payment.or')}</span>
          <span className="h-px flex-1 bg-ink-100" />
        </div>
      </div>
    )
  const bump = () => setEligible((c) => c + 1)
  return (
    <div className={eligible > 0 ? 'space-y-2.5' : 'hidden'}>
      <ApplePayButton {...props} onEligible={bump} />
      <GooglePayButton {...props} onEligible={bump} />
      <div className="relative flex items-center gap-3 pt-1">
        <span className="h-px flex-1 bg-ink-100" />
        <span className="text-xs font-medium uppercase tracking-wide text-ink-400">{t('payment.or')}</span>
        <span className="h-px flex-1 bg-ink-100" />
      </div>
    </div>
  )
}

// ── Apple Pay ──────────────────────────────────────────────────────────────
function ApplePayButton({ config, amountCents, createOrder, onApprove, onError, onEligible }: WalletProps) {
  const createOrderRef = useRef(createOrder)
  const onApproveRef = useRef(onApprove)
  const onErrorRef = useRef(onError)
  createOrderRef.current = createOrder
  onApproveRef.current = onApprove
  onErrorRef.current = onError

  const [ready, setReady] = useState(false)
  const cfgRef = useRef<any>(null)
  const applepayRef = useRef<any>(null)

  useEffect(() => {
    let cancelled = false
    if (!config.clientId || typeof window === 'undefined') return
    // Apple Pay (PayPal web flow). Re-enabled in the native WKWebView now that
    // app.garagesanmarco.it is verified for Apple Pay in PayPal (the app also carries the
    // merchant.it.garagesanmarco.app entitlement, so ApplePaySession is available in the WebView).
    // The eligibility checks below (canMakePayments + Applepay().config().isEligible) keep the
    // button hidden if it can't actually run, so this can never crash the payment step.
    // canMakePayments() THROWS (not returns false) in an insecure context — e.g. the native
    // Capacitor webview at capacitor://localhost. Guard it so Apple Pay being unavailable can
    // never crash the whole payment step.
    let canApplePay = false
    try {
      canApplePay = Boolean(window.ApplePaySession && window.ApplePaySession.canMakePayments())
    } catch {
      canApplePay = false
    }
    if (!canApplePay) return
    loadPayPal(config.clientId, { currency: config.currency, intent: config.intent })
      .then(async (paypal) => {
        if (cancelled || !paypal.Applepay) return
        const applepay = paypal.Applepay()
        const cfg = await applepay.config()
        if (cancelled || !cfg?.isEligible) return
        applepayRef.current = applepay
        cfgRef.current = cfg
        setReady(true)
        onEligible?.()
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.clientId])

  async function pay() {
    const applepay = applepayRef.current
    const cfg = cfgRef.current
    if (!applepay || !cfg || !window.ApplePaySession) return
    try {
      const paymentRequest = {
        countryCode: cfg.countryCode,
        currencyCode: config.currency || 'EUR',
        merchantCapabilities: cfg.merchantCapabilities,
        supportedNetworks: cfg.supportedNetworks,
        requiredBillingContactFields: ['name', 'phone', 'email', 'postalAddress'],
        total: { label: MERCHANT_NAME, amount: (amountCents / 100).toFixed(2), type: 'final' },
      }
      const session = new window.ApplePaySession(4, paymentRequest)
      session.onvalidatemerchant = async (event: any) => {
        try {
          const payload = await applepay.validateMerchant({ validationUrl: event.validationURL })
          session.completeMerchantValidation(payload.merchantSession)
        } catch (e) {
          session.abort()
          onErrorRef.current?.(e)
        }
      }
      session.onpaymentauthorized = async (event: any) => {
        try {
          const orderId = await createOrderRef.current()
          await applepay.confirmOrder({ orderId, token: event.payment.token, billingContact: event.payment.billingContact })
          session.completePayment(window.ApplePaySession.STATUS_SUCCESS)
          await onApproveRef.current(orderId)
        } catch (e) {
          session.completePayment(window.ApplePaySession.STATUS_FAILURE)
          onErrorRef.current?.(e)
        }
      }
      session.oncancel = () => {}
      session.begin()
    } catch (e) {
      onErrorRef.current?.(e)
    }
  }

  if (!ready) return null
  return (
    <button
      type="button"
      onClick={pay}
      aria-label="Apple Pay"
      className="apple-pay-button flex h-12 w-full items-center justify-center rounded-md bg-black text-base font-semibold text-white"
    >
       Pay
    </button>
  )
}

// ── Google Pay ─────────────────────────────────────────────────────────────
function GooglePayButton({ config, amountCents, createOrder, onApprove, onError, onEligible }: WalletProps) {
  const createOrderRef = useRef(createOrder)
  const onApproveRef = useRef(onApprove)
  const onErrorRef = useRef(onError)
  createOrderRef.current = createOrder
  onApproveRef.current = onApprove
  onErrorRef.current = onError

  const hostRef = useRef<HTMLDivElement>(null)
  const [ready, setReady] = useState(false)
  const stateRef = useRef<{ googlepay: any; client: any; cfg: any } | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!config.clientId) return
    Promise.all([loadPayPal(config.clientId, { currency: config.currency, intent: config.intent }), loadGooglePayApi()])
      .then(async ([paypal]) => {
        if (cancelled || !paypal.Googlepay || !window.google?.payments?.api) return
        const googlepay = paypal.Googlepay()
        const cfg = await googlepay.config()
        if (cancelled) return
        const client = new window.google.payments.api.PaymentsClient({
          environment: config.environment === 'live' ? 'PRODUCTION' : 'TEST',
        })
        const isReady = await client.isReadyToPay({
          apiVersion: cfg.apiVersion,
          apiVersionMinor: cfg.apiVersionMinor,
          allowedPaymentMethods: cfg.allowedPaymentMethods,
        })
        if (cancelled || !isReady?.result) return
        stateRef.current = { googlepay, client, cfg }
        setReady(true)
        onEligible?.()
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.clientId])

  // Render Google's official button once eligible.
  useEffect(() => {
    if (!ready || !stateRef.current || !hostRef.current) return
    const { client } = stateRef.current
    hostRef.current.innerHTML = ''
    try {
      const button = client.createButton({
        onClick: pay,
        buttonType: 'pay',
        buttonSizeMode: 'fill',
        buttonRadius: 8,
      })
      hostRef.current.appendChild(button)
    } catch (e) {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready])

  async function pay() {
    const st = stateRef.current
    if (!st) return
    const { googlepay, client, cfg } = st
    try {
      const paymentDataRequest = {
        apiVersion: cfg.apiVersion,
        apiVersionMinor: cfg.apiVersionMinor,
        allowedPaymentMethods: cfg.allowedPaymentMethods,
        merchantInfo: cfg.merchantInfo,
        transactionInfo: {
          countryCode: cfg.countryCode || 'IT',
          currencyCode: config.currency || 'EUR',
          totalPriceStatus: 'FINAL',
          totalPrice: (amountCents / 100).toFixed(2),
          totalPriceLabel: MERCHANT_NAME,
        },
      }
      const paymentData = await client.loadPaymentData(paymentDataRequest)
      const orderId = await createOrderRef.current()
      const result = await googlepay.confirmOrder({ orderId, paymentMethodData: paymentData.paymentMethodData })
      if (result?.status === 'APPROVED' || result?.status === 'PAYER_ACTION_REQUIRED') {
        await onApproveRef.current(orderId)
      } else {
        onErrorRef.current?.(new Error('Google Pay not approved'))
      }
    } catch (e: any) {
      if (e?.statusCode === 'CANCELED') return
      onErrorRef.current?.(e)
    }
  }

  if (!ready) return null
  return <div ref={hostRef} className="h-12 w-full [&_button]:!h-12 [&_button]:!w-full" />
}
